import { useCallback } from 'react';
import { useRootStore } from '../../../providers/RootStoreProvider';
import AssetFilters from '../models/filtersAndSorts/assetFilters';
import FileTypeFilter from '../models/filtersAndSorts/fileTypeFilter';
import FilterOption from '../models/filtersAndSorts/filterOption';
import LabelFilter from '../models/filtersAndSorts/labelFilter';
import RoundFilter from '../models/filtersAndSorts/roundFilter';
import { FileType, SelectedLabelID, SelectedUserID } from '../models/filtersAndSorts/types';
import UserAssignedFilter from '../models/filtersAndSorts/userAssignedFilter';

const useFilterStore = ( projectID: number ) => {
	const { filterStore } = useRootStore();

	const projectConfig = filterStore.getProjectFilterConfiguration( projectID );
	const { searchText, filterOptions, isFilterSidePanelOpen } = projectConfig;

	const filters = new AssetFilters( { searchText, filterOptions } );

	const setFiltersSearchText = useCallback(
		( aSearchText: string ) => filterStore.setProjectFilterSearchText( projectID, aSearchText ),
		[ projectID ]
	);

	const addFilterOption = useCallback( ( filter: FilterOption ) => {
		if ( filters.isFilteringBy( filter ) ) {
			return;
		}

		let newFilterOptions;
		if ( filter instanceof LabelFilter ) {
			newFilterOptions = filters.filterOptions.filter( option => !( option instanceof LabelFilter ) );
		} else if ( filter instanceof RoundFilter ) {
			newFilterOptions = filters.filterOptions.filter( option => !( option instanceof RoundFilter ) );
		} else if ( filter instanceof UserAssignedFilter ) {
			newFilterOptions = filters.filterOptions.filter( option => !( option instanceof UserAssignedFilter ) );
		} else if ( filter instanceof FileTypeFilter ) {
			newFilterOptions = filters.filterOptions.filter( option => !( option instanceof FileTypeFilter ) );
		} else {
			newFilterOptions = [ ...filters.filterOptions ];
		}

		newFilterOptions.push( filter );

		filterStore.setProjectFilterOptions( projectID, newFilterOptions );
	}, [ filters.stringOfFilterOptionIds, projectID ] );

	const removeFilterOption = useCallback( ( filter: FilterOption ) => {
		const newFilterOptions = filters.filterOptions.filter( option => option.id !== filter.id );

		filterStore.setProjectFilterOptions( projectID, newFilterOptions );
	}, [ filters.stringOfFilterOptionIds, projectID ] );

	const removeLabelFilter = useCallback( () => {
		const newFilterOptions = filters.filterOptions.filter( option => !( option instanceof LabelFilter ) );

		filterStore.setProjectFilterOptions( projectID, newFilterOptions );
	}, [ filters.stringOfFilterOptionIds, projectID ] );

	const removeUserAssignedFilter = useCallback( () => {
		const newFilterOptions = filters.filterOptions.filter( option => !( option instanceof UserAssignedFilter ) );

		filterStore.setProjectFilterOptions( projectID, newFilterOptions );
	}, [ filters.stringOfFilterOptionIds, projectID ] );

	const toggleFilterOption = useCallback( ( filter: FilterOption ) => {
		if ( filters.isFilteringBy( filter ) ) {
			removeFilterOption( filter );
		} else {
			addFilterOption( filter );
		}
	}, [ filters.stringOfFilterOptionIds, projectID ] );

	const resetFilters = useCallback(
		() => {
			filterStore.setProjectFilterOptions( projectID, [] );
		}, [ projectID ]
	);

	const changeLabelFilter = useCallback( (
		selectedLabelsIds: SelectedLabelID[], matchAll: boolean
	) => {
		const filter = new LabelFilter( selectedLabelsIds, matchAll );

		if ( filter.isEmpty ) {
			removeLabelFilter();
		} else {
			toggleFilterOption( filter );
		}
	}, [ toggleFilterOption, removeLabelFilter ] );

	const changeUserAssignedFilter = useCallback( ( userIds: SelectedUserID[] ) => {
		const filter = new UserAssignedFilter( userIds );

		if ( filter.isEmpty ) {
			removeUserAssignedFilter();
		} else {
			toggleFilterOption( filter );
		}
	}, [ toggleFilterOption, removeUserAssignedFilter ] );

	const changeRoundFilter = useCallback( ( roundNumbers: number[] ) => {
		if ( roundNumbers.length === 0 ) {
			const existingRoundFilter = filters.filterOptions.find( option => option instanceof RoundFilter );
			if ( existingRoundFilter ) {
				removeFilterOption( existingRoundFilter );
			}
		} else {
			const filter = new RoundFilter( roundNumbers );
			addFilterOption( filter );
		}
	}, [ filters.filterOptions, addFilterOption, removeFilterOption ] );

	const removeFileTypeFilter = useCallback( () => {
		const newFilterOptions = filters.filterOptions.filter( option => !( option instanceof FileTypeFilter ) );

		filterStore.setProjectFilterOptions( projectID, newFilterOptions );
	}, [ filters.stringOfFilterOptionIds, projectID ] );

	const changeFileTypeFilter = useCallback( ( fileType: FileType | null ) => {
		if ( fileType === null ) {
			removeFileTypeFilter();
		} else {
			const filter = new FileTypeFilter( fileType );
			addFilterOption( filter );
		}
	}, [ addFilterOption, removeFileTypeFilter ] );

	const setFilterSidePanelOpen = useCallback(
		( isOpen: boolean ) => filterStore.setProjectFilterSidePanelOpen( projectID, isOpen ),
		[ projectID ]
	);

	return {
		filters,
		setFiltersSearchText,
		addFilterOption,
		removeFilterOption,
		toggleFilterOption,
		changeLabelFilter,
		changeUserAssignedFilter,
		changeRoundFilter,
		changeFileTypeFilter,
		resetFilters,
		isFilterSidePanelOpen,
		setFilterSidePanelOpen
	}
};

export default useFilterStore;
